import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { TrendingUp, ArrowRight, BarChart3, Cog, Award } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROISimulator = () => {
  const [loading, setLoading] = useState(false);
  const [roiData, setRoiData] = useState({
    strategy_name: 'QRM',
    throughput_reduction_percent: 25,
    error_reduction_percent: 15,
    customer_satisfaction_increase_percent: 20
  });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleSliderChange = (field, value) => {
    setRoiData(prev => ({
      ...prev,
      [field]: value[0]
    }));
  };

  const calculateROI = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        throw new Error('User session not found');
      }

      const response = await axios.post(`${API}/roi-simulation`, {
        ...roiData,
        user_id: userId
      });
      
      setResults(response.data);
    } catch (error) {
      console.error('Failed to calculate ROI:', error);
      
      // Fallback calculation
      const baseRevenue = 1000000;
      const throughputImpact = roiData.throughput_reduction_percent / 100 * 0.15;
      const errorImpact = roiData.error_reduction_percent / 100 * 0.08;
      const satisfactionImpact = roiData.customer_satisfaction_increase_percent / 100 * 0.12;
      const totalImpact = throughputImpact + errorImpact + satisfactionImpact;
      const annualProfitIncrease = baseRevenue * totalImpact;
      const roiPercent = totalImpact * 100;
      const paybackPeriod = 12 / Math.max(totalImpact * 12, 0.1);

      setResults({
        strategy_name: roiData.strategy_name,
        throughput_reduction_percent: roiData.throughput_reduction_percent,
        error_reduction_percent: roiData.error_reduction_percent,
        customer_satisfaction_increase_percent: roiData.customer_satisfaction_increase_percent,
        calculated_roi_percent: roiPercent,
        payback_period_months: paybackPeriod,
        annual_profit_increase: annualProfitIncrease
      });
    } finally {
      setLoading(false);
    }
  };

  const strategies = [
    { value: 'QRM', label: 'Quick Responsive Manufacturing' },
    { value: 'Lean', label: 'Lean Manufacturing' },
    { value: 'TOC', label: 'Theory of Constraints' },
    { value: 'Kaizen', label: 'Kaizen' }, 
    { value: '5S', label: '5S' },
    { value: 'Digital Work', label: 'Digital Work' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">ROI Impact Simulator</h1>
            <p className="text-xl text-gray-600">Calculate the financial impact of your chosen strategy</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Input Controls */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Cog className="mr-3 h-6 w-6 text-blue-600" />
                Strategy Configuration
              </h2>

              <div className="space-y-8">
                <div>
                  <Label className="text-lg font-semibold mb-4 block">Selected Strategy</Label>
                  <Select 
                    value={roiData.strategy_name}
                    onValueChange={(value) => setRoiData(prev => ({...prev, strategy_name: value}))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {strategies.map((strategy) => (
                        <SelectItem key={strategy.value} value={strategy.value}>
                          {strategy.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    Throughput Time Reduction: {roiData.throughput_reduction_percent}%
                  </Label>
                  <Slider
                    value={[roiData.throughput_reduction_percent]}
                    onValueChange={(value) => handleSliderChange('throughput_reduction_percent', value)}
                    max={70}
                    min={0}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>0%</span>
                    <span>35%</span>
                    <span>70%</span>
                  </div>
                </div>

                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    Error Reduction: {roiData.error_reduction_percent}%
                  </Label>
                  <Slider
                    value={[roiData.error_reduction_percent]}
                    onValueChange={(value) => handleSliderChange('error_reduction_percent', value)}
                    max={50}
                    min={0}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                  </div>
                </div>

                <div>
                  <Label className="text-lg font-semibold mb-4 block">
                    Customer Satisfaction Increase: {roiData.customer_satisfaction_increase_percent}%
                  </Label>
                  <Slider
                    value={[roiData.customer_satisfaction_increase_percent]}
                    onValueChange={(value) => handleSliderChange('customer_satisfaction_increase_percent', value)}
                    max={50}
                    min={0}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                  </div>
                </div>

                <Button 
                  onClick={calculateROI}
                  size="lg"
                  disabled={loading}
                  className="w-full py-6 text-lg font-semibold bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-3"></div>
                      Calculating...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="mr-3 h-6 w-6" />
                      Calculate ROI Impact
                    </>
                  )}
                </Button>
              </div>
            </Card>

            {/* Results Display */}
            <div className="space-y-6">
              {results && (
                <>
                  <Card className="p-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <TrendingUp className="mr-3 h-6 w-6 text-green-600" />
                      Financial Impact
                    </h2>
                    
                    <div className="grid grid-cols-1 gap-6">
                      <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                        <div className="text-4xl font-bold text-green-600 mb-2">
                          {results.calculated_roi_percent.toFixed(1)}%
                        </div>
                        <div className="text-gray-600">Total ROI</div>
                      </div>
                      
                      <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                        <div className="text-3xl font-bold text-blue-600 mb-2">
                          {formatCurrency(results.annual_profit_increase)}
                        </div>
                        <div className="text-gray-600">Annual Profit Increase</div>
                      </div>
                      
                      <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                        <div className="text-3xl font-bold text-purple-600 mb-2">
                          {results.payback_period_months.toFixed(1)}
                        </div>
                        <div className="text-gray-600">Payback Period (months)</div>
                      </div>
                    </div>
                  </Card>

                  {/* Impact Breakdown */}
                  <Card className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Impact Breakdown</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                        <span className="font-medium text-gray-700">Throughput Improvement</span>
                        <span className="font-bold text-blue-600">{results.throughput_reduction_percent}%</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                        <span className="font-medium text-gray-700">Error Reduction</span>
                        <span className="font-bold text-green-600">{results.error_reduction_percent}%</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                        <span className="font-medium text-gray-700">Customer Satisfaction</span>
                        <span className="font-bold text-purple-600">{results.customer_satisfaction_increase_percent}%</span>
                      </div>
                    </div>
                  </Card>

                  {/* Success Comparison */}
                  <Card className="p-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <Award className="mr-2 h-5 w-5 text-yellow-600" />
                      Industry Benchmark Comparison
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Your projected results vs. industry benchmark from successful QRM implementations:
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="font-semibold text-gray-900">Your Projection:</div>
                        <div className="text-blue-600">{results.calculated_roi_percent.toFixed(1)}% ROI</div>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Best-in-Class:</div>
                        <div className="text-green-600">Significant revenue increase</div>
                      </div>
                    </div>
                  </Card>
                </>
              )}

              {!results && (
                <Card className="p-8 text-center">
                  <div className="text-gray-400 mb-4">
                    <BarChart3 className="h-16 w-16 mx-auto" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-600 mb-2">Ready to Calculate</h3>
                  <p className="text-gray-500">
                    Adjust the sliders on the left and click "Calculate ROI Impact" to see your potential results.
                  </p>
                </Card>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {results && (
            <div className="mt-12 text-center space-x-4">
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => window.location.href = '/recommendations'}
                className="px-8 py-3"
              >
                ← Back to Recommendations
              </Button>
              <Button 
                size="lg"
                className="px-8 py-3 bg-green-600 hover:bg-green-700"
                onClick={() => window.location.href = '/implementation-plan'}
              >
                Start Implementation Planning
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ROISimulator;
